(() => {
  "use strict";

  const NAMESPACE = "quntis.support_hub";
  const VERSION = 1;
  const MIN_HEIGHT = 900;
  const PAGE_VERSION = "2026-07-24";
  const MAX_RESULT_COUNT = 25;
  const MAX_QUERY_LENGTH = 256;
  const VARIANT_CODE_PATTERN = /^(?:F(?:23BK|40BK|41GY1?|45BK|51BK|52BK|DC41(?:GY|WT))|FR52GY|G(?:41(?:GY|PK|WT)|42GY|51GY|66BK)|I(?:40BK|50BK|51(?:GY|PK)|66BK|MS45BK)|S(?:60BK|78GY|82BK))$/;
  const MODEL_NUMBER_PATTERN = /^(?:LI-HY-02(?:01-(?:BK|GY)|05-BK|06-(?:BK|GY)|08-(?:BK|GY)|09-(?:BK|GY)|10-(?:BK|WT)|11-BK|12-(?:BK|GY|PK)|13-(?:BK|GY)|14-(?:BK|GY|PK|WT))|PA005S2|PHX002|HT8885|YS-TL23064)$/;
  const LOCALE_PATTERN = /^(?:en|de|es|ja|fr|it|pt|ar|nl|sv|pl|tr|zh)(?:-(?:[A-Z]{2}|[0-9]{3}))?$/;
  const ALLOWED_EVENTS = new Set([
    "hub_view",
    "route_change",
    "search_submitted",
    "search_zero_results",
    "purchase_channel_prompt",
    "purchase_channel_select",
    "series_selected",
    "product_selected",
    "manual_open",
    "manual_download_click",
    "manual_load_error",
    "faq_open",
    "contact_support_click",
    "video_open",
  ]);
  const SAFE_FIELDS = [
    "page_version",
    "variant_code",
    "model_number",
    "locale",
    "source_section",
    "result_count",
    "query_type",
    "query_length",
    "market",
  ];
  const SAFE_ROUTES = new Set(["videos", "videos2", "manuals", "support"]);
  const SAFE_MARKETS = new Set(["global", "amazon_in"]);
  const SAFE_QUERY_TYPES = new Set(["empty", "identifier", "product_name"]);
  const SAFE_SOURCE_SECTIONS = new Set([
    "page",
    "videos",
    "videos2",
    "manuals",
    "support",
    "model_search",
    "manual_reader",
    "contact_form",
    "faq",
  ]);
  const COUNT_FIELDS = new Set(["result_count", "query_length"]);
  const installedFrames = new WeakSet();

  function safeFieldValue(field, value) {
    if (COUNT_FIELDS.has(field)) {
      const maximum = field === "result_count" ? MAX_RESULT_COUNT : MAX_QUERY_LENGTH;
      return Number.isSafeInteger(value) && value >= 0 && value <= maximum
        ? value
        : undefined;
    }
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (field === "page_version") {
      return trimmed === PAGE_VERSION ? trimmed : undefined;
    }
    if (field === "variant_code") {
      return trimmed.length <= 8 && VARIANT_CODE_PATTERN.test(trimmed)
        ? trimmed
        : undefined;
    }
    if (field === "model_number") {
      return trimmed.length <= 16 && MODEL_NUMBER_PATTERN.test(trimmed)
        ? trimmed
        : undefined;
    }
    if (field === "locale") {
      const [base, region, extra] = trimmed.replace(/_/g, "-").split("-");
      if (extra) return undefined;
      const canonical = region
        ? `${base.toLowerCase()}-${/^[0-9]{3}$/.test(region) ? region : region.toUpperCase()}`
        : base.toLowerCase();
      return canonical.length <= 8 && LOCALE_PATTERN.test(canonical)
        ? canonical
        : undefined;
    }
    if (field === "market" && !SAFE_MARKETS.has(trimmed)) return undefined;
    if (field === "query_type" && !SAFE_QUERY_TYPES.has(trimmed)) return undefined;
    if (field === "source_section" && !SAFE_SOURCE_SECTIONS.has(trimmed)) {
      return undefined;
    }
    return trimmed;
  }

  function pickSafeFields(payload) {
    try {
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
      const safe = {};
      for (const field of SAFE_FIELDS) {
        if (!Object.prototype.hasOwnProperty.call(payload, field)) continue;
        const value = safeFieldValue(field, payload[field]);
        if (value === undefined) return null;
        safe[field] = value;
      }
      return safe;
    } catch (_error) {
      return null;
    }
  }

  function measuredHeight(frame) {
    try {
      const doc = frame.contentDocument;
      if (!doc) return 0;
      return Math.max(
        Number(doc.body?.scrollHeight) || 0,
        Number(doc.documentElement?.scrollHeight) || 0,
      );
    } catch (_error) {
      return 0;
    }
  }

  function setFrameHeight(frame, requestedHeight = 0) {
    const requested = Number(requestedHeight);
    const safeRequested = Number.isFinite(requested) && requested > 0 ? requested : 0;
    const measured = safeRequested || measuredHeight(frame);
    const height = Math.ceil(Math.max(MIN_HEIGHT, measured));
    frame.style.height = `${height}px`;
    return height;
  }

  function syncRoute(host, route) {
    if (!SAFE_ROUTES.has(route)) return;
    try {
      const url = new URL(host.location.href);
      url.hash = route;
      if (new URL(host.location.href).hash === url.hash) return;
      host.history.replaceState(null, "", url.toString());
    } catch (_error) {
      // History sync is progressive enhancement.
    }
  }

  function syncFrameRoute(frame, route) {
    if (!SAFE_ROUTES.has(route)) return;
    try {
      const nextHash = `#${route}`;
      if (frame.contentWindow?.location?.hash !== nextHash) {
        frame.contentWindow.location.hash = nextHash;
      }
    } catch (_error) {
      // The srcdoc route remains usable if parent-to-child sync is unavailable.
    }
  }

  function publish(host, eventName, payload) {
    const normalized = {
      event_name: eventName,
      ...pickSafeFields(payload),
    };
    try {
      host.Shopify?.analytics?.publish?.(
        "quntis_support_hub:interaction",
        normalized,
      );
    } catch (_error) {
      // Analytics must never block support functionality.
    }
  }

  function attachSameOriginFallback(frame, host, heightState) {
    let observer;
    const update = () => {
      if (!heightState.hasTrustedChildHeight) setFrameHeight(frame);
    };
    const onLoad = () => {
      heightState.hasTrustedChildHeight = false;
      update();
      try {
        const Observer = host.ResizeObserver || window.ResizeObserver;
        const root = frame.contentDocument?.documentElement;
        if (typeof Observer === "function" && root) {
          observer?.disconnect?.();
          observer = new Observer(update);
          observer.observe(root);
        }
      } catch (_error) {
        // Cross-origin frames rely on content_height messages.
      }
    };
    frame.addEventListener?.("load", onLoad);
    return () => observer?.disconnect?.();
  }

  function install(frame, host = window) {
    if (!frame || installedFrames.has(frame)) return () => {};
    installedFrames.add(frame);
    const heightState = { hasTrustedChildHeight: false };

    const onMessage = (messageEvent) => {
      try {
        if (messageEvent.source !== frame.contentWindow) return;
        const data = messageEvent.data;
        if (
          !data
          || typeof data !== "object"
          || data.namespace !== NAMESPACE
          || data.version !== VERSION
        ) return;

        if (data.type === "content_height") {
          if (
            typeof data.height !== "number"
            || !Number.isFinite(data.height)
            || data.height <= 0
          ) return;
          heightState.hasTrustedChildHeight = true;
          setFrameHeight(frame, data.height);
          return;
        }

        if (!ALLOWED_EVENTS.has(data.event)) return;
        const safePayload = pickSafeFields(data.payload);
        if (safePayload === null) return;
        if (data.event === "route_change") {
          syncRoute(host, safePayload.source_section);
        }
        publish(host, data.event, safePayload);
      } catch (_error) {
        // Untrusted message getters cannot escape the analytics boundary.
      }
    };

    const onParentHashChange = () => {
      try {
        const route = new URL(host.location.href).hash.slice(1);
        syncFrameRoute(frame, route);
      } catch (_error) {
        // Invalid parent URLs cannot affect the iframe route.
      }
    };
    const onRouteLinkClick = (event) => {
      const link = event.target?.closest?.("a[data-quntis-support-route]");
      const route = link?.dataset?.quntisSupportRoute;
      if (!SAFE_ROUTES.has(route)) return;
      syncRoute(host, route);
      syncFrameRoute(frame, route);
    };

    host.addEventListener("message", onMessage);
    host.addEventListener("hashchange", onParentHashChange);
    host.document?.addEventListener?.("click", onRouteLinkClick);
    const disconnectFallback = attachSameOriginFallback(frame, host, heightState);
    return () => {
      host.removeEventListener?.("message", onMessage);
      host.removeEventListener?.("hashchange", onParentHashChange);
      host.document?.removeEventListener?.("click", onRouteLinkClick);
      disconnectFallback();
    };
  }

  function installAll(root = document, host = window) {
    root.querySelectorAll?.("iframe[data-quntis-support-frame]").forEach((frame) => {
      install(frame, host);
    });
  }

  window.QuntisSupportHubBridge = Object.freeze({
    install,
    installAll,
    pickSafeFields,
    setFrameHeight,
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => installAll(), { once: true });
  } else {
    installAll();
  }
})();
